import { z } from "zod";
import { LineItemTypeSchema } from "@remi/enums/line-item-type";
import { PricingTypeSchema } from "@remi/enums/pricing-type";
import { SiteVisitReinspectionFaultSchema } from "@remi/enums/site-visit-reinspection-fault";
import { TaskType } from "@remi/enums/task-type";

const CreateWorkOrderLineItemSchema = z.object({
  lineItemType: z.object({
    name: LineItemTypeSchema,
    pricingType: PricingTypeSchema,
    unit: z.string(),
  }),
  pricingType: PricingTypeSchema,
  lineItemTypeName: LineItemTypeSchema,
  customName: z.string().nullable(),
  autoQuantity: z.number(),
  quantity: z.number(),
  unitPrice: z.number(),
  autoUnitPrice: z.number(),
  isAutoLineItem: z.boolean(),
  description: z.string().nullable(),
  isRepair: z.boolean(),
  // Origin tag preserved through the form → submit → DB chain so CO-origin rows
  // survive a CREATE_WORK_ORDER reset + rebuild (ENFRANK-3540).
  changeOrderId: z.string().nullish(),
});

// Required would throw on every historical row — db/extensions.ts parses stored attempts with an
// uncatching .parse. Omitted from either schema it is silently Zod-stripped instead of erroring.
const reinspectionFault = SiteVisitReinspectionFaultSchema.nullish();

export const CreateWorkOrderTaskAttemptFormSchema = z.object({
  type: z.literal(TaskType.CREATE_WORK_ORDER),
  specialInstructions: z.string(),
  workOrderLineItems: z.array(CreateWorkOrderLineItemSchema),
  updateReason: z.string().max(5000).optional(),
  fault: reinspectionFault,
  concessionSelections: z.array(z.object({ concessionId: z.string(), include: z.boolean() })).optional(),
});

export type CreateWorkOrderTaskAttemptForm = z.infer<typeof CreateWorkOrderTaskAttemptFormSchema>;

export const CreateWorkOrderTaskAttemptSchema = z.object({
  type: z.literal(TaskType.CREATE_WORK_ORDER),
  workOrderId: z.number(),
  totalAmount: z.number(),
  lineItemCount: z.number().int().nonnegative(),
  updateReason: z.string().max(5000).optional(),
  fault: reinspectionFault,
  rejectionReviewTaskId: z.string().optional(),
  keptConcessionIds: z.array(z.string()).optional(),
});

export type CreateWorkOrderTaskAttempt = z.infer<typeof CreateWorkOrderTaskAttemptSchema>;
